import ConditionalLoader from '@Guidelines/Component/Loader/ConditionalLoader';
import { theme } from '@Guidelines/Theme/theme';
import type { DrillAction } from '@UI/Pages/Administration/AiCosts/CostTabs/SubjectCosts/Helpers/resolveDrillAction';
import { css } from '@emotion/react';
import { ChevronRight } from 'lucide-react';
import type * as React from 'react';
import TonePill, { type PillTone } from '@UI/Pages/Administration/AiCosts/Shared/TonePill';
import { Button } from '@verclam/ui-kit';

const cardStyle = (isInverted: boolean) => css`
	display: flex;
	flex-direction: column;
	gap: 0.375rem;
	padding: 0.875rem 1rem;
	border-radius: ${theme.radii.small};
	background-color: ${isInverted ? theme.colors.surfaceInverted : theme.colors.surfaceMuted};
	color: ${isInverted ? theme.colors.inkOnDark : theme.colors.ink};
`;

const headerStyle = css`
	display: flex;
	align-items: baseline;
	justify-content: space-between;
	gap: 0.75rem;
`;

const titleStyle = (isMuted: boolean) => css`
	font-family: ${theme.fonts.body};
	font-size: ${theme.fontSizes.large};
	font-weight: ${isMuted ? theme.fontWeights.medium : theme.fontWeights.semibold};
	font-style: ${isMuted ? 'italic' : 'normal'};
	${isMuted ? `color: ${theme.colors.inkMuted};` : ''}
`;

const amountStyle = css`
	display: flex;
	align-items: center;
	gap: 0.25rem;
	font-family: ${theme.fonts.mono};
	font-size: ${theme.fontSizes.large};
	font-weight: ${theme.fontWeights.semibold};
`;

const lineStyle = css`
	margin: 0;
	font-family: ${theme.fonts.body};
	font-size: ${theme.fontSizes.medium};
	opacity: 0.85;
`;

type Props = {
	readonly title: string;
	readonly amount: string;
	readonly lines: readonly string[];
	readonly chip?: string | null;
	readonly chipTone?: PillTone;
	readonly isMuted?: boolean;
	/** The closing Total card is drawn on the inverted surface. */
	readonly isInverted?: boolean;
	readonly drill?: DrillAction | null;
	/** Closes the card, under the chip. */
	readonly footer?: React.ReactNode;
};

/** A row of a table, drawn as a card on a narrow screen. */
const CostSummaryCard: React.FC<Props> = ({
	title,
	amount,
	lines,
	chip = null,
	chipTone = 'warning',
	isMuted = false,
	isInverted = false,
	drill = null,
	footer = null,
}) => {
	return (
		<article css={cardStyle(isInverted)}>
			<div css={headerStyle}>
				<span css={titleStyle(isMuted)}>{title}</span>
				<span css={amountStyle}>
					{amount}
					<ConditionalLoader data={drill}>
						{(action) => (
							<Button
								type="button"
								text
								rounded
								severity="secondary"
								icon={<ChevronRight size={16} />}
								aria-label={action.ariaLabel}
								onClick={action.run}
							/>
						)}
					</ConditionalLoader>
				</span>
			</div>
			{lines.map((line) => (
				<p key={line} css={lineStyle}>
					{line}
				</p>
			))}
			<ConditionalLoader data={chip}>
				{(text) => (
					<div>
						<TonePill text={text} tone={chipTone} />
					</div>
				)}
			</ConditionalLoader>
			<ConditionalLoader data={footer}>{(content) => <div>{content}</div>}</ConditionalLoader>
		</article>
	);
};

export default CostSummaryCard;
